import React from 'react';
import { Alert, Box, Button, LinearProgress, Fade, Typography } from '@mui/material';
import { Cancel, RouteOutlined, Schedule } from '@mui/icons-material';

export interface RoutingProgressIndicatorProps {
  isVisible: boolean;
  isCalculating: boolean;
  progress: 'idle' | 'started' | 'processing' | 'finalizing';
  progressPercent?: number; // Dynamic progress percentage
  timeElapsed: number;
  targetDuration?: number; // Target duration for the route calculation
  error: string | null;
  canCancel: boolean;
  onCancel?: () => void;
  onRetry?: () => void;
  onDeleteRoute?: () => void;
  onUndo?: () => void;
  canUndo?: boolean;
  // DetailPanel state for positioning
  isDetailPanelOpen?: boolean;
  isDetailPanelExpanded?: boolean;
}

const PROGRESS_MESSAGES = {
  idle: 'Ready to calculate route',
  started: 'Starting route calculation...',
  processing: 'Calculating route...',
  finalizing: 'Finalizing route details...'
};

// Time-based messages for processing state
const PROCESSING_MESSAGES = [
  'Calculating route...',           // 0-60% of target time (6/10)
  'Processing elevation data...',   // 60-80% of target time (2/10) 
  'Analyzing surface conditions...', // 80-90% of target time (1/10)
  'Optimizing Route...'            // 90%+ of target time (everything exceeding)
];

const PROGRESS_VALUES = {
  idle: 0,
  started: 20,
  processing: 70,
  finalizing: 95
};

export const RoutingProgressIndicator: React.FC<RoutingProgressIndicatorProps> = ({
  isVisible,
  isCalculating,
  progress,
  progressPercent,
  timeElapsed,
  targetDuration,
  error,
  canCancel,
  onCancel,
  onRetry,
  onDeleteRoute,
  onUndo,
  canUndo = false,
  isDetailPanelOpen = false,
  isDetailPanelExpanded = false
}) => {
  if (!isVisible && !error) return null;

  // Time-based message selection for processing state
  const getDynamicMessage = () => {
    if (error) return error;
    if (progress === 'processing' && targetDuration) {
      const timeProgress = timeElapsed / (targetDuration / 1000); // Convert ms to seconds
      
      if (timeProgress < 0.6) {
        return PROCESSING_MESSAGES[0]; // "Calculating route..." (0-60% of target time)
      } else if (timeProgress < 0.8) {
        return PROCESSING_MESSAGES[1]; // "Processing elevation data..." (60-80% of target time)
      } else if (timeProgress < 0.9) {
        return PROCESSING_MESSAGES[2]; // "Analyzing surface conditions..." (80-90% of target time)
      } else {
        return PROCESSING_MESSAGES[3]; // "Optimizing Route..." (90%+ of target time)
      }
    }
    return PROGRESS_MESSAGES[progress];
  };

  // Get helpful explanation based on error message
  const getErrorExplanation = () => {
    if (!error) return null;
    
    const errorLower = error.toLowerCase();
    
    if (errorLower.includes('timeout') || errorLower.includes('60-second')) {
      return 'The route calculation took too long. Try placing waypoints closer to roads or reducing the number of waypoints.';
    }
    if (errorLower.includes('network') || errorLower.includes('connection')) {
      return 'Unable to connect to the routing service. Please check your internet connection.';
    }
    if (errorLower.includes('waypoint') || errorLower.includes('accessible')) {
      return 'One or more waypoints may be too far from accessible roads. Try adjusting their positions.';
    }
    if (errorLower.includes('failed') || errorLower.includes('error')) {
      return 'An unexpected error occurred. Please try again or adjust your route points.';
    }
    
    return 'Please try again or adjust your waypoints.';
  };

  const message = getDynamicMessage();
  const errorExplanation = getErrorExplanation();
  const progressValue = error ? 0 : (progressPercent ?? PROGRESS_VALUES[progress]);
  const showProgressBar = isCalculating && !error;

  // Calculate vertical position based on DetailPanel state
  const getVerticalPosition = () => {
    if (isDetailPanelOpen) {
      if (isDetailPanelExpanded) {
        return '15%'; // Higher up when DetailPanel is fullscreen
      } else {
        return '30%'; // Higher up when DetailPanel is regular height
      }
    }
    return '50%'; // Higher up when DetailPanel is closed (was 70%)
  };

  return (
    <Fade in={isVisible || !!error} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: getVerticalPosition(),
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2000,
          minWidth: 350,
          maxWidth: 450, // Increased for better text readability
          // Glass-morphism with app colors
          background: 'linear-gradient(135deg, rgba(225, 232, 225, 0.95) 0%, rgba(225, 232, 225, 0.9) 100%)',
          backdropFilter: 'blur(12px)',
          borderRadius: 2,
          border: '1px solid rgba(14, 44, 38, 0.2)',
          boxShadow: `
            0 3px 12px rgba(14, 44, 38, 0.12),
            0 1px 4px rgba(14, 44, 38, 0.08)
          `,
          // Smooth animation
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Alert
          severity={error ? 'error' : 'info'}
          icon={<RouteOutlined />}
          sx={{
            '& .MuiAlert-message': {
              width: '100%',
              fontSize: '0.85rem',
              fontWeight: 500,
            },
            '& .MuiAlert-icon': {
              fontSize: '1.1rem',
              opacity: 0.8,
              color: error ? 'var(--color-error)' : 'var(--color-info)',
            },
            // App-themed styling with reduced padding
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            padding: '12px 16px',
            color: error ? 'var(--color-error)' : 'var(--color-info)',
            boxShadow: 'none',
          }}
        >
          <Box sx={{ p: 0.25 }}>
            {/* Main error/progress message */}
            <Box sx={{ 
              mb: error ? 0.5 : (showProgressBar ? 0.5 : 0.3),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.8
            }}>
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: error ? 'var(--color-error)' : 'var(--color-info)',
                  letterSpacing: '0.2px'
                }}
              >
                {message}
              </Typography>
              
              {/* Elapsed time during calculation - inline with message */}
              {isCalculating && !error && timeElapsed > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.3
                }}>
                  <Schedule sx={{ fontSize: '0.75rem', opacity: 0.7, color: 'var(--color-info)' }} />
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 600,
                    color: 'var(--color-info)'
                  }}>
                    {timeElapsed}s
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Error explanation text */}
            {error && errorExplanation && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'var(--color-medium-gray)',
                  mb: 1.5,
                  lineHeight: 1.4
                }}
              >
                {errorExplanation}
              </Typography>
            )}
            
            {/* Action buttons for error state - below text */}
            {error && (
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 0.5
              }}>
                {/* Undo button - first - dark green filled */}
                {onUndo && canUndo && (
                  <Button
                    size="small"
                    onClick={onUndo}
                    variant="contained"
                    sx={{ 
                      minWidth: 'auto',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 0.6,
                      fontSize: '0.8rem',
                      backgroundColor: 'var(--color-primary)', // Dark green
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'var(--color-accent)', // Lighter green on hover
                      }
                    }}
                  >
                    Undo
                  </Button>
                )}
                {/* Retry button - second - green outline */}
                {onRetry && (
                  <Button
                    size="small"
                    onClick={onRetry}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.6,
                      fontSize: '0.8rem',
                      borderColor: 'var(--color-accent)', // Green outline
                      color: 'var(--color-accent)',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'rgba(30, 77, 64, 0.1)', // Light green background on hover
                      }
                    }}
                  >
                    Retry
                  </Button>
                )}
                {/* Clear button - third */}
                {onDeleteRoute && (
                  <Button
                    size="small"
                    onClick={onDeleteRoute}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.6,
                      fontSize: '0.8rem',
                      borderColor: 'var(--color-error)',
                      color: 'var(--color-error)',
                      '&:hover': {
                        borderColor: 'var(--color-error)',
                        backgroundColor: 'rgba(122, 28, 28, 0.1)',
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            )}
            
            {/* Elapsed time for errors */}
            {error && timeElapsed > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center', 
                gap: 0.3,
                mt: 1
              }}>
                <Schedule sx={{ fontSize: '0.7rem', opacity: 0.6, color: 'var(--color-medium-gray)' }} />
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem', 
                  fontWeight: 500,
                  color: 'var(--color-medium-gray)'
                }}>
                  {timeElapsed}s elapsed
                </Typography>
              </Box>
            )}
            
            {/* Compact progress bar */}
            {showProgressBar && (
              <Box sx={{ mb: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" sx={{ 
                    color: 'var(--color-info)', 
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    fontSize: '0.6rem'
                  }}>
                    Progress
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'var(--color-info)', 
                    fontWeight: 600,
                    fontSize: '0.65rem'
                  }}>
                    {Math.round(progressValue)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 5,
                    borderRadius: 2.5,
                    backgroundColor: 'var(--color-light-gray)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2.5,
                      background: 'linear-gradient(90deg, var(--color-accent) 0%, var(--color-primary) 100%)',
                      animation: 'pulse 2s ease-in-out infinite',
                    },
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
              </Box>
            )}
            
            {/* Cancel button for active calculation */}
            {canCancel && isCalculating && onCancel && !error && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                mt: 0.5,
                mb: 0.5
              }}>
                <Button
                  size="small"
                  onClick={onCancel}
                  startIcon={<Cancel sx={{ fontSize: '0.9rem' }} />}
                  variant="outlined"
                  sx={{ 
                    minWidth: 'auto',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderColor: 'var(--color-neutral)',
                    color: 'var(--color-neutral)',
                    '&:hover': {
                      borderColor: 'var(--color-accent)',
                      backgroundColor: 'rgba(30, 77, 64, 0.1)',
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </Alert>
      </Box>
    </Fade>
  );
};

export default RoutingProgressIndicator;